import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useTheme } from '../../context/ThemeContext';
import './Scene3D.css';

const LERP = 0.06;
const LERP_CAM = 0.03;

// Smooth float + mouse parallax with lerp; optional invert for opposite motion
function FloatMesh({
  children,
  speed = 1.5,
  floatIntensity = 0.5,
  rotationIntensity = 0.3,
  pointerFactor = 1.2,
  invertPointer = false,
  tiltTowardPointer = true,
  position = [0, 0, 0],
  ...props
}) {
  const ref = useRef();
  const basePos = useRef([...position]);
  const currentPos = useRef([...position]);
  const currentRot = useRef([0, 0, 0]);
  const targetRot = useRef([0, 0, 0]);

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime * speed;
    const px = state.pointer.x;
    const py = state.pointer.y;
    const s = invertPointer ? -1 : 1;

    const targetX = basePos.current[0] + px * pointerFactor * s;
    const targetY = basePos.current[1] + Math.sin(t) * 0.025 * floatIntensity + py * pointerFactor * s;
    const targetZ = basePos.current[2];

    currentPos.current[0] += (targetX - currentPos.current[0]) * LERP;
    currentPos.current[1] += (targetY - currentPos.current[1]) * LERP;
    currentPos.current[2] = targetZ;

    ref.current.position.x = currentPos.current[0];
    ref.current.position.y = currentPos.current[1];
    ref.current.position.z = currentPos.current[2];

    if (tiltTowardPointer) {
      targetRot.current[0] = Math.sin(t * 0.5) * 0.08 * rotationIntensity + py * 0.2 * s;
      targetRot.current[1] += 0.0015 * rotationIntensity + px * 0.025 * s;
    } else {
      targetRot.current[0] = Math.sin(t * 0.5) * 0.1 * rotationIntensity;
      targetRot.current[1] += 0.002 * rotationIntensity;
    }
    currentRot.current[0] += (targetRot.current[0] - currentRot.current[0]) * LERP;
    currentRot.current[1] += (targetRot.current[1] - currentRot.current[1]) * LERP;
    ref.current.rotation.x = currentRot.current[0];
    ref.current.rotation.y = currentRot.current[1];
  });

  return (
    <group ref={ref} position={position} {...props}>
      {children}
    </group>
  );
}

// Starfield: smooth rotation follow + subtle pulse
function Starfield({ theme }) {
  const count = 2000;
  const ref = useRef();
  const rotX = useRef(0);
  const rotY = useRef(0);
  const [positions, colors] = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const isLight = theme === 'light';
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      pos[i3] = (Math.random() - 0.5) * 80;
      pos[i3 + 1] = (Math.random() - 0.5) * 80;
      pos[i3 + 2] = (Math.random() - 0.5) * 50 - 20;
      const c = isLight ? 0.25 + Math.random() * 0.25 : 0.45 + Math.random() * 0.45;
      col[i3] = col[i3 + 1] = c;
      col[i3 + 2] = isLight ? 0.55 : 0.95;
    }
    return [pos, col];
  }, [theme]);

  useFrame((state) => {
    if (!ref.current) return;
    const px = state.pointer.x * 0.015;
    const py = state.pointer.y * 0.015;
    rotX.current += (py - rotX.current) * LERP;
    rotY.current += (px - rotY.current) * LERP;
    ref.current.rotation.x = rotX.current;
    ref.current.rotation.y = rotY.current;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-color" count={count} array={colors} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial
        size={theme === 'light' ? 0.1 : 0.14}
        vertexColors
        transparent
        opacity={theme === 'light' ? 0.45 : 0.85}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

// Particles: drift opposite to pointer for depth
function FloatingParticles({ theme }) {
  const count = 120;
  const ref = useRef();
  const rotX = useRef(0);
  const rotY = useRef(0);
  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count * 3; i += 3) {
      pos[i] = (Math.random() - 0.5) * 20;
      pos[i + 1] = (Math.random() - 0.5) * 20;
      pos[i + 2] = (Math.random() - 0.5) * 15 - 5;
    }
    return pos;
  }, []);

  useFrame((state) => {
    if (!ref.current) return;
    rotX.current += (-state.pointer.y * 0.04 - rotX.current) * LERP;
    rotY.current += (-state.pointer.x * 0.04 - rotY.current) * LERP;
    ref.current.rotation.x = rotX.current;
    ref.current.rotation.y = rotY.current;
  });

  const particleColor = theme === 'light' ? '#0d9488' : '#14b8a6';
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.07} color={particleColor} transparent opacity={0.7} sizeAttenuation depthWrite={false} />
    </points>
  );
}

function AnimatedSphere({ theme }) {
  const color = theme === 'light' ? '#0d9488' : '#2dd4bf';
  return (
    <FloatMesh position={[3, 1, -4]} speed={2} floatIntensity={1} rotationIntensity={0.4} pointerFactor={1.4} tiltTowardPointer>
      <mesh>
        <sphereGeometry args={[1.2, 64, 64]} />
        <meshStandardMaterial color={color} roughness={0.25} metalness={0.75} transparent opacity={0.14} />
      </mesh>
    </FloatMesh>
  );
}

function TorusRing({ theme }) {
  const color = theme === 'light' ? '#7c3aed' : '#a78bfa';
  return (
    <FloatMesh position={[-4, -1, -5]} speed={1.5} floatIntensity={0.7} rotationIntensity={0.25} pointerFactor={1.6} invertPointer tiltTowardPointer>
      <mesh rotation={[Math.PI / 4, 0, Math.PI / 6]}>
        <torusKnotGeometry args={[1, 0.3, 128, 32]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.15} roughness={0.35} metalness={0.65} transparent opacity={0.11} />
      </mesh>
    </FloatMesh>
  );
}

function SecondSphere({ theme }) {
  const color = theme === 'light' ? '#14b8a6' : '#14b8a6';
  return (
    <FloatMesh position={[-2, 2, -6]} speed={1.8} floatIntensity={0.8} rotationIntensity={0.28} pointerFactor={1.1}>
      <mesh>
        <sphereGeometry args={[0.8, 32, 32]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.12} roughness={0.4} metalness={0.6} transparent opacity={0.1} />
      </mesh>
    </FloatMesh>
  );
}

function Icosahedron({ theme }) {
  const color = theme === 'light' ? '#059669' : '#2dd4bf';
  return (
    <FloatMesh position={[4, -2, -7]} speed={2.2} floatIntensity={0.5} rotationIntensity={0.5} pointerFactor={1.3} invertPointer tiltTowardPointer>
      <mesh rotation={[0.3, 0.5, 0]}>
        <icosahedronGeometry args={[1, 0]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.08} roughness={0.5} metalness={0.5} transparent opacity={0.07} wireframe />
      </mesh>
    </FloatMesh>
  );
}

function CameraRig() {
  const camX = useRef(0);
  const camY = useRef(0);
  useFrame((state) => {
    const tx = state.pointer.x * 0.4;
    const ty = state.pointer.y * 0.4;
    camX.current += (tx - camX.current) * LERP_CAM;
    camY.current += (ty - camY.current) * LERP_CAM;
    state.camera.position.x = camX.current;
    state.camera.position.y = camY.current;
    state.camera.position.z = 8;
    state.camera.lookAt(0, 0, 0);
    state.camera.updateProjectionMatrix();
  });
  return null;
}

function SceneContent() {
  const { theme } = useTheme();
  const bgColor = theme === 'light' ? '#e7e5e4' : '#0c0c0c';

  return (
    <>
      <color attach="background" args={[bgColor]} />
      <CameraRig />
      <ambientLight intensity={theme === 'light' ? 0.45 : 0.14} />
      <directionalLight position={[10, 10, 5]} intensity={theme === 'light' ? 0.75 : 0.4} color="#ffffff" />
      <pointLight position={[-10, -10, -5]} color={theme === 'light' ? '#14b8a6' : '#2dd4bf'} intensity={0.28} />
      <pointLight position={[10, 5, -5]} color={theme === 'light' ? '#a78bfa' : '#a78bfa'} intensity={0.18} />
      <Starfield theme={theme} />
      <FloatingParticles theme={theme} />
      <AnimatedSphere theme={theme} />
      <TorusRing theme={theme} />
      <SecondSphere theme={theme} />
      <Icosahedron theme={theme} />
    </>
  );
}

export default function Scene3D() {
  return (
    <div className="scene3d-wrapper">
      <Canvas camera={{ position: [0, 0, 8], fov: 60 }} gl={{ antialias: true, alpha: true }} dpr={[1, 2]}>
        <SceneContent />
      </Canvas>
    </div>
  );
}
